import { useAuth } from '../contexts/AuthContext';
import { boardPreferenceKey, preferredBoard, rememberBoard } from '../lib/boardPreference';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { Opportunity, OpportunityBoard, OpportunityBoardStatus, LoadingState } from '../types';
import { api } from '../lib/api';

// Helper function to fetch all pages of paginated data
type PaginatedResponse<T> = {
  count: number;
  next: string | null;
  results: T[];
};

async function fetchAllPages<T>(url: string): Promise<T[]> {
  let allResults: T[] = [];
  let nextUrl: string | null = url;
  
  while (nextUrl) {
    // Explicitly type the response to avoid TypeScript circular reference error
    const response: { data: T[] | PaginatedResponse<T>; status: number } = await api.get<T[] | PaginatedResponse<T>>(nextUrl);
    
    if (Array.isArray(response.data)) {
      // Non-paginated response
      return response.data;
    } else if (response.data && 'results' in response.data) {
      // Paginated response
      const paginatedData = response.data as PaginatedResponse<T>;
      allResults = allResults.concat(paginatedData.results);
      // Extract next page URL if it exists
      if (paginatedData.next) {
        // Convert absolute URL to relative path
        const urlObj: URL = new URL(paginatedData.next);
        nextUrl = urlObj.pathname + urlObj.search;
      } else {
        nextUrl = null;
      }
    } else {
      console.warn('Unexpected response format:', response.data);
      return [];
    }
  }
  
  return allResults;
}

// Hook to fetch all opportunities for organization-wide metrics
export function useAllOpportunities() {
  return useQuery<Opportunity[]>({
    queryKey: ['all-opportunities'],
    queryFn: async () => {
      // Fetch ALL pages to ensure we get all opportunities for metrics
      const allOpportunities = await fetchAllPages<Opportunity>('/api/crm/opportunities/');
      return allOpportunities;
    },
    staleTime: 60 * 1000, // Cache for 1 minute
  });
}

export function useContactOpportunities(contactId: number) {
  return useQuery({
    queryKey: ['opportunities', 'contact', contactId],
    queryFn: async () => {
      const response = await api.get<Opportunity[]>(`/api/crm/opportunities/?contact=${contactId}`);
      return response.data;
    },
    enabled: !!contactId,
  });
}

export function useCreateOpportunity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      const response = await api.createOpportunity(data);
      return response.data;
    },
    onSuccess: (opportunity) => {
      // Invalidate all opportunity-related queries to refresh kanban boards
      queryClient.invalidateQueries({ queryKey: ['opportunities'] });
      queryClient.invalidateQueries({ queryKey: ['opportunity-boards'] });
      if (opportunity.contact) {
        queryClient.invalidateQueries({ queryKey: ['opportunities', 'contact', opportunity.contact] });
      }
    },
    onError: (error) => {
    },
  });
}

export function useUpdateOpportunity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => {
      const response = await api.updateOpportunity(id, data);
      return response.data;
    },
    onSuccess: (opportunity) => {
      // Invalidate both general and contact-specific queries
      queryClient.invalidateQueries({ queryKey: ['opportunities'] });
      queryClient.invalidateQueries({ queryKey: ['opportunities', opportunity.id] });
      if (opportunity.contact) {
        queryClient.invalidateQueries({ queryKey: ['opportunities', 'contact', opportunity.contact] });
      }
    },
    onError: (error) => {
    },
  });
}

export function useDeleteOpportunity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const response = await api.deleteOpportunity(id);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['opportunities'] });
    },
  });
}

// Kanban Board Hooks (similar to Jobs)
export interface UseOpportunitiesKanbanResult {
  opportunities: Opportunity[];
  boards: OpportunityBoard[];
  statuses: OpportunityBoardStatus[];
  loading: LoadingState;
  selectedBoard: number | null;
  setSelectedBoard: (boardId: number) => void;
  updateOpportunityStatus: (opportunityId: number, statusId: number) => Promise<void>;
  createOpportunity: (opportunity: Partial<Opportunity>) => Promise<void>;
  updateOpportunity: (opportunityId: number, opportunity: Partial<Opportunity>) => Promise<void>;
  deleteOpportunity: (opportunityId: number) => Promise<void>;
  refreshData: () => Promise<void>;
}

export const useOpportunitiesKanban = (): UseOpportunitiesKanbanResult => {
  // Initialize with empty arrays to prevent map errors
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [boards, setBoards] = useState<OpportunityBoard[]>([]);
  const [statuses, setStatuses] = useState<OpportunityBoardStatus[]>([]);
  const [selectedBoard, setSelectedBoard] = useState<number | null>(null);
  const { user } = useAuth();
  const preferenceKey = boardPreferenceKey('opportunities', user);
  const boardsVersion = useRef(0);
  const requestVersion = useRef(0);
  useEffect(
    () => () => {
      requestVersion.current += 1;
      boardsVersion.current += 1;
    },
    [],
  );
  const [loading, setLoading] = useState<LoadingState>({
    isLoading: true,
    error: null,
  });

  const handleError = (error: any) => {
    setLoading({
      isLoading: false,
      error: error.message || 'An unexpected error occurred',
    });
  };

  const loadBoards = useCallback(async () => {
    const version = ++boardsVersion.current;
    requestVersion.current += 1;
    setSelectedBoard(null);
    setBoards([]);
    setStatuses([]);
    setOpportunities([]);
    try {
      const response = await api.get<{ results: OpportunityBoard[] }>('/api/crm/opportunity-boards/');
      const available = Array.isArray(response.data) ? response.data : response.data.results || [];
      if (version !== boardsVersion.current) return;
      setBoards(available);
      setSelectedBoard(preferredBoard(available, preferenceKey));
      if (!available.length) setLoading({ isLoading: false, error: null });
    } catch (error) {
      if (version === boardsVersion.current) handleError(error);
        }
  }, [preferenceKey]);

  const loadKanbanData = useCallback(async (boardId: number) => {
    const version = ++requestVersion.current;
    setLoading({ isLoading: true, error: null });
    try {
      const [statusesData, opportunitiesData] = await Promise.all([
        fetchAllPages<OpportunityBoardStatus>(`/api/crm/opportunity-boards/${boardId}/statuses/`),
        fetchAllPages<Opportunity>(`/api/crm/opportunities/?board=${boardId}`),
      ]);
      if (version !== requestVersion.current) return;
      setStatuses(statusesData);
      setOpportunities(opportunitiesData);
      setLoading({ isLoading: false, error: null });
    } catch (error) {
      if (version !== requestVersion.current) return;
      handleError(error);
      setStatuses([]);
      setOpportunities([]);
    }
  }, []);

  const updateOpportunityStatus = useCallback(async (opportunityId: number, statusId: number) => {
    try {
      await api.patch(`/api/crm/opportunities/${opportunityId}/`, { board_status: statusId });
      
      // Update the opportunity in the local state
      setOpportunities(prevOpportunities => 
        prevOpportunities.map(opportunity => 
          opportunity.id === opportunityId ? { ...opportunity, board_status: statusId } : opportunity
        )
      );
    } catch (error) {
      handleError(error);
      throw error; // Re-throw to handle in drag-drop
    }
  }, []);

  const createOpportunity = useCallback(async (opportunityData: Partial<Opportunity>) => {
    try {
      if (!selectedBoard) {
        throw new Error('No board selected');
      }
      
      const response = await api.post<Opportunity>('/api/crm/opportunities/', {
        ...opportunityData,
        board: selectedBoard,
      });
      
      setOpportunities(prevOpportunities => [...prevOpportunities, response.data]);
    } catch (error) {
      handleError(error);
      throw error;
    }
  }, [selectedBoard]);

  const updateOpportunity = useCallback(async (opportunityId: number, opportunityData: Partial<Opportunity>) => {
    try {
      const response = await api.patch<Opportunity>(`/api/crm/opportunities/${opportunityId}/`, opportunityData);
      
      setOpportunities(prevOpportunities => 
        prevOpportunities.map(opportunity => 
          opportunity.id === opportunityId ? { ...opportunity, ...response.data } : opportunity
        )
      );
    } catch (error) {
      handleError(error);
      throw error;
    }
  }, []);

  const deleteOpportunity = useCallback(async (opportunityId: number) => {
    try {
      await api.delete(`/api/crm/opportunities/${opportunityId}/`);
      
      setOpportunities(prevOpportunities => prevOpportunities.filter(opportunity => opportunity.id !== opportunityId));
    } catch (error) {
      handleError(error);
      throw error;
    }
  }, []);

  const refreshData = useCallback(async () => {
    if (selectedBoard) {
      await loadKanbanData(selectedBoard);
    }
  }, [selectedBoard, loadKanbanData]);

  const handleBoardChange = useCallback(
    (boardId: number) => {
      if (!boards.some((board) => board.id === boardId)) return;
      rememberBoard(preferenceKey, boardId);
      requestVersion.current += 1;
      setOpportunities([]);
      setStatuses([]);
      setLoading({ isLoading: true, error: null });
    setSelectedBoard(boardId);
    },
    [boards, preferenceKey],
  );

  // Load initial data
  useEffect(() => {
    loadBoards();
  }, [loadBoards]);

  // Load kanban data when selected board changes
  useEffect(() => {
    if (selectedBoard) {
      loadKanbanData(selectedBoard);
    }
  }, [selectedBoard, loadKanbanData]);

  return {
    opportunities,
    boards,
    statuses,
    loading,
    selectedBoard,
    setSelectedBoard: handleBoardChange,
    updateOpportunityStatus,
    createOpportunity,
    updateOpportunity,
    deleteOpportunity,
    refreshData,
  };
};

// React Query hooks for Opportunity Board functionality
export const useOpportunityBoards = () => {
  return useQuery({
    queryKey: ['opportunity-boards'],
    queryFn: async () => {
      const response = await api.get<{ results: OpportunityBoard[] }>('/api/crm/opportunity-boards/');
      return Array.isArray(response.data) ? response.data : response.data.results || [];
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes since boards don't change often
  });
};

export const useOpportunityBoardsWithStatuses = () => {
  return useQuery({
    queryKey: ['opportunity-boards-with-statuses'],
    queryFn: async () => {
      const response = await api.get('/api/crm/opportunity-boards/with-statuses/');
      return response.data as { boards: any[] };
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes since boards/statuses don't change often
  });
};